<template>
  <v-container class="py-6" style="max-width: 640px;">

    <!-- Back -->
    <v-btn variant="text" prepend-icon="mdi-arrow-left" class="mb-4 ml-n2" to="/matches">
      Înapoi
    </v-btn>

    <!-- Loading match -->
    <div v-if="loadingMatch" class="text-center py-10">
      <v-progress-circular indeterminate color="success" />
    </div>

    <!-- Match not found -->
    <v-alert v-else-if="notFound" type="error" variant="tonal">
      Raportul nu a fost găsit.
    </v-alert>

    <template v-else>

      <!-- Score header -->
      <v-card rounded="lg" elevation="1" class="pa-6 mb-4 text-center">
        <div class="text-h2 font-weight-bold text-success mb-1">{{ match.scores?.score }}%</div>
        <div class="text-subtitle-1 text-medium-emphasis">compatibilitate cu {{ otherPersonName }}</div>

        <!-- Sub-score summary -->
        <template v-if="scoreBreakdown.length">
          <v-divider class="my-3" />
          <div class="text-left">
            <div
              v-for="d in scoreBreakdown" :key="d.key"
              class="d-flex align-center justify-space-between py-1"
            >
              <span class="text-body-2 text-medium-emphasis">{{ d.label }}</span>
              <span
                v-if="d.key === 'val' || premiumUnlocked"
                class="text-body-2 font-weight-medium"
                :class="d.alert ? 'text-warning' : ''"
              >
                {{ d.pct }}%
                <v-icon v-if="d.alert" color="warning" size="14" class="ml-1">mdi-alert-outline</v-icon>
              </span>
              <v-icon v-else color="medium-emphasis" size="14">mdi-lock-outline</v-icon>
            </div>
          </div>
        </template>

        <p class="text-caption text-medium-emphasis mt-3 mb-0">
          Scorul reflectă o combinație de similaritate și compatibilitate psihologică — un punct de plecare, nu o concluzie.
          Scopul este să te ajute să identifici mai ușor cu cine ai putea construi o relație bună.
        </p>
      </v-card>

      <!-- Report status banners (shown while the pipeline is running) -->
      <template v-if="isGenerating">
        <v-card rounded="lg" elevation="0" variant="tonal" color="info" class="pa-5 mb-4 text-center">
          <v-progress-circular indeterminate color="info" size="24" class="mb-3" />
          <div class="text-body-2">Se generează raportul de compatibilitate…</div>
          <div class="text-caption text-medium-emphasis mt-1">Generarea ar putea dura până la 2 minute.</div>
        </v-card>
      </template>
      <template v-else-if="isStuck">
        <v-alert type="warning" variant="tonal" class="mb-4">
          <div class="mb-2">Generarea raportului durează mai mult decât de obicei.</div>
          <v-btn color="warning" variant="tonal" size="small" :loading="retrying" @click="retryReport">
            Încearcă din nou
          </v-btn>
        </v-alert>
      </template>
      <template v-else-if="reportFailed">
        <v-alert type="error" variant="tonal" class="mb-4">
          <div class="mb-2">Generarea raportului a eșuat.</div>
          <v-btn color="error" variant="tonal" size="small" :loading="retrying" @click="retryReport">
            Încearcă din nou
          </v-btn>
        </v-alert>
      </template>

      <!-- 1. Values — always visible (free baseline) -->
      <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="success" class="mr-2">mdi-compass-rose</v-icon>
          <span class="text-subtitle-2 font-weight-bold text-uppercase text-medium-emphasis">Valori</span>
        </div>

        <template v-if="scoreMap.val">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-body-2 text-medium-emphasis">Compatibilitate</span>
            <span class="text-body-2 font-weight-medium" :class="scoreMap.val.alert ? `text-${scoreMap.val.alertColor}` : ''">
              {{ scoreMap.val.pct }}%
              <v-icon v-if="scoreMap.val.alert" :color="scoreMap.val.alertColor" size="16" class="ml-1">mdi-alert-outline</v-icon>
            </span>
          </div>
          <v-progress-linear
            :model-value="scoreMap.val.pct"
            :color="scoreMap.val.alert ? scoreMap.val.alertColor : 'success'"
            bg-color="surface-variant"
            rounded
            height="6"
          />
        </template>

        <!-- Schwartz values vector chart -->
        <template v-if="valVectors.me != null">
          <v-divider class="my-4" />
          <div class="text-subtitle-2 font-weight-bold text-uppercase text-medium-emphasis mb-1">
            Direcția valorică
          </div>
          <div class="text-caption text-medium-emphasis mb-3">
            Unde pointează „centrul de greutate" al valorilor fiecăruia pe harta Schwartz.
            Cu cât săgețile sunt mai apropiate, cu atât profilurile sunt mai aliniate.
          </div>
          <ValuesVectorChart
            :vec-angle-a="valVectors.me"
            :vec-angle-b="valVectors.other"
            :phi="match.scores.details.val.phi"
            label-a="Tu"
            :label-b="otherPersonName.split(' ')[0]"
          />
        </template>

        <v-alert
          v-if="match.scores?.thresholds?.valuesConflict"
          type="warning" variant="tonal" density="compact" class="mt-3 text-body-2"
        >
          Diferențe fundamentale în sistemul de valori — compatibilitatea pe această dimensiune este semnificativ redusă.
        </v-alert>

        <template v-if="valueInsights">
          <v-divider class="my-4" />
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mb-2">Valori comune</div>
              <v-chip
                v-for="v in valueInsights.shared" :key="v"
                size="small" color="success" variant="tonal" class="mr-1 mb-1"
              >{{ v }}</v-chip>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold mb-2">Valori opuse</div>
              <template v-if="valueInsights.opposing.length">
                <v-chip
                  v-for="v in valueInsights.opposing" :key="v"
                  size="small" color="error" variant="tonal" class="mr-1 mb-1"
                >{{ v }}</v-chip>
              </template>
              <span v-else class="text-caption text-medium-emphasis">Niciuna identificată</span>
            </v-col>
          </v-row>
        </template>

        <template v-if="report">
          <v-divider class="my-4" />
          <div class="md-content" v-html="md(report.values)" />
        </template>
      </v-card>

      <!-- Premium teaser CTA — shown until unlocked -->
      <v-card v-if="report && !premiumUnlocked" rounded="lg" elevation="1" class="pa-6 mb-4 text-center">
        <v-icon color="warning" size="36" class="mb-3">mdi-lock-open-variant-outline</v-icon>
        <div class="text-body-1 font-weight-medium mb-1">Vrei să vezi și restul?</div>
        <div class="text-body-2 text-medium-emphasis mb-4">
          Personalitate, atașament, comunicare și etapă de viață — raportul tău complet de compatibilitate.
        </div>
        <v-btn color="success" variant="elevated" rounded="pill" block @click="unlockPremium">
          Deblochează tot raportul
        </v-btn>
        <div class="text-caption text-medium-emphasis mt-3">Gratuit în perioada beta.</div>
      </v-card>

      <!-- 2. Personality -->
      <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="primary" class="mr-2">mdi-account-outline</v-icon>
          <span class="text-subtitle-2 font-weight-bold text-uppercase text-medium-emphasis">Personalitate</span>
          <v-icon v-if="!premiumUnlocked" color="medium-emphasis" size="16" class="ml-auto">mdi-lock-outline</v-icon>
        </div>

        <template v-if="scoreMap.b5 && premiumUnlocked">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-body-2 text-medium-emphasis">Compatibilitate</span>
            <span class="text-body-2 font-weight-medium">{{ scoreMap.b5.pct }}%</span>
          </div>
          <v-progress-linear
            :model-value="scoreMap.b5.pct"
            color="success"
            bg-color="surface-variant"
            rounded
            height="6"
          />
        </template>

        <template v-if="report">
          <v-divider class="my-4" />
          <div :class="{ 'report-gated': !premiumUnlocked }">
            <div class="md-content" v-html="md(report.personality)" />
            <div v-if="!premiumUnlocked" class="report-gated-overlay" />
          </div>
        </template>
      </v-card>

      <!-- 3. Attachment -->
      <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="warning" class="mr-2">mdi-shield-outline</v-icon>
          <span class="text-subtitle-2 font-weight-bold text-uppercase text-medium-emphasis">Atașament</span>
          <v-icon v-if="!premiumUnlocked" color="medium-emphasis" size="16" class="ml-auto">mdi-lock-outline</v-icon>
        </div>

        <template v-if="scoreMap.att && premiumUnlocked">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-body-2 text-medium-emphasis">Compatibilitate</span>
            <span class="text-body-2 font-weight-medium" :class="scoreMap.att.alert ? `text-${scoreMap.att.alertColor}` : ''">
              {{ scoreMap.att.pct }}%
              <v-icon v-if="scoreMap.att.alert" :color="scoreMap.att.alertColor" size="16" class="ml-1">mdi-alert-outline</v-icon>
            </span>
          </div>
          <v-progress-linear
            :model-value="scoreMap.att.pct"
            :color="scoreMap.att.alert ? scoreMap.att.alertColor : 'success'"
            bg-color="surface-variant"
            rounded
            height="6"
          />
        </template>

        <v-alert
          v-if="match.scores?.thresholds?.toxicAttachment"
          type="error" variant="tonal" density="compact" class="mt-3 text-body-2"
        >
          Combinație de stiluri de atașament care necesită conștientizare activă — anxios + evitant poate genera dinamici dificile.
        </v-alert>

        <template v-if="report">
          <v-divider class="my-4" />
          <div :class="{ 'report-gated': !premiumUnlocked }">
            <div class="md-content" v-html="md(report.attachment)" />
            <div v-if="!premiumUnlocked" class="report-gated-overlay" />
          </div>
        </template>
      </v-card>

      <!-- 4. Communication -->
      <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="info" class="mr-2">mdi-chat-outline</v-icon>
          <span class="text-subtitle-2 font-weight-bold text-uppercase text-medium-emphasis">Comunicare</span>
          <v-icon v-if="!premiumUnlocked" color="medium-emphasis" size="16" class="ml-auto">mdi-lock-outline</v-icon>
        </div>

        <template v-if="scoreMap.com && premiumUnlocked">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-body-2 text-medium-emphasis">Compatibilitate</span>
            <span class="text-body-2 font-weight-medium">{{ scoreMap.com.pct }}%</span>
          </div>
          <v-progress-linear
            :model-value="scoreMap.com.pct"
            color="success"
            bg-color="surface-variant"
            rounded
            height="6"
          />
        </template>

        <template v-if="report">
          <v-divider class="my-4" />
          <div :class="{ 'report-gated': !premiumUnlocked }">
            <div class="md-content" v-html="md(report.communication)" />
            <div v-if="!premiumUnlocked" class="report-gated-overlay" />
          </div>
        </template>
      </v-card>

      <!-- 5. Age / life stage -->
      <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="secondary" class="mr-2">mdi-timeline-outline</v-icon>
          <span class="text-subtitle-2 font-weight-bold text-uppercase text-medium-emphasis">Etapă de viață</span>
          <v-icon v-if="!premiumUnlocked" color="medium-emphasis" size="16" class="ml-auto">mdi-lock-outline</v-icon>
        </div>

        <template v-if="scoreMap.age && premiumUnlocked">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-body-2 text-medium-emphasis">Compatibilitate</span>
            <span class="text-body-2 font-weight-medium">{{ scoreMap.age.pct }}%</span>
          </div>
          <v-progress-linear
            :model-value="scoreMap.age.pct"
            color="success"
            bg-color="surface-variant"
            rounded
            height="6"
          />
        </template>

        <template v-if="report">
          <v-divider class="my-4" />
          <div :class="{ 'report-gated': !premiumUnlocked }">
            <div class="md-content" v-html="md(report.age)" />
            <div v-if="!premiumUnlocked" class="report-gated-overlay" />
          </div>
        </template>
      </v-card>

      <match-feedback-card
        v-if="match.reportState === 'DONE'"
        :match-id="match.id"
        :initial-rate="myRate"
        :initial-comment="myRateComment"
      />

    </template>

  </v-container>
</template>

<script>
import apiService from '@/services/ApiService'
import { appStore } from '@/stores/AppStore'
import ValuesVectorChart from '@/components/user/ValuesVectorChart.vue'
import MatchFeedbackCard from '@/components/user/MatchFeedbackCard.vue'
import { marked } from 'marked'

// Polling config — interval and timeout driven by VUE_APP_POLL_INTERVAL_MS / VUE_APP_POLL_TIMEOUT_MS.
// After POLL_MAX attempts without a terminal state (DONE/FAILED), isStuck becomes
// true and the user is shown a manual retry button instead of the endless spinner.
const POLL_INTERVAL = parseInt(process.env.VUE_APP_POLL_INTERVAL_MS ?? '2000')
const POLL_MAX      = Math.ceil(parseInt(process.env.VUE_APP_POLL_TIMEOUT_MS ?? '120000') / POLL_INTERVAL)

const VALUE_ORDER = [
  'power', 'achievement', 'hedonism', 'stimulation', 'self-direction',
  'universalism', 'benevolence', 'tradition', 'conformity', 'security',
]

/**
 * Pairwise circumflex relation between any two Schwartz values.
 * Based on Schwartz's circular motivational model (Schwartz, 1992).
 * Row = value A, column = value B (column order matches VALUE_ORDER).
 *
 * Legend:
 *   ID  — same value (identity)
 *   C+  — adjacent on the circumflex; highly compatible motivations
 *   C   — nearby; generally compatible, minor tension possible
 *   T   — tension; motivations pull in somewhat different directions
 *   O   — opposed; motivations are in clear conflict
 *   O!  — maximally opposed; diametrically opposite on the circumflex
 *
 * Used to detect opposing values between two people:
 * a value from person B is "opposing" if its relation to any of person A's
 * top values is 'O' or 'O!'.
 */
const RELATION_MATRIX = {
//                      PO     AC     HE     ST     SD     UN     BE     TR     CO     SE
  'power':         [ 'ID',  'C+',  'C',   'T',   'O',   'O!',  'O',   'T',   'C',   'C+'  ],
  'achievement':   [ 'C+',  'ID',  'C+',  'C',   'T',   'O',   'O!',  'O',   'T',   'C'   ],
  'hedonism':      [ 'C',   'C+',  'ID',  'C+',  'C',   'T',   'O',   'O!',  'O',   'T'   ],
  'stimulation':   [ 'T',   'C',   'C+',  'ID',  'C+',  'C',   'T',   'O',   'O!',  'O'   ],
  'self-direction':[ 'O',   'T',   'C',   'C+',  'ID',  'C+',  'C',   'T',   'O',   'O!'  ],
  'universalism':  [ 'O!',  'O',   'T',   'C',   'C+',  'ID',  'C+',  'C',   'T',   'O'   ],
  'benevolence':   [ 'O',   'O!',  'O',   'T',   'C',   'C+',  'ID',  'C+',  'C',   'T'   ],
  'tradition':     [ 'T',   'O',   'O!',  'O',   'T',   'C',   'C+',  'ID',  'C+',  'C'   ],
  'conformity':    [ 'C',   'T',   'O',   'O!',  'O',   'T',   'C',   'C+',  'ID',  'C+'  ],
  'security':      [ 'C+',  'C',   'T',   'O',   'O!',  'O',   'T',   'C',   'C+',  'ID'  ],
}

function areOpposed(a, b) {
  const rel = RELATION_MATRIX[a]?.[VALUE_ORDER.indexOf(b)]
  return rel === 'O' || rel === 'O!'
}

export default {
  name: 'MatchReportView',
  components: { ValuesVectorChart, MatchFeedbackCard },

  data() {
    return {
      loadingMatch: true,
      notFound: false,
      match: null,
      report: null,
      pollCount: 0,
      pollTimer: null,
      pollExhausted: false,
      retrying: false,
      premiumUnlocked: appStore().user?.premiumUnlockedAt != null,
    }
  },

  computed: {
    store() {
      return appStore()
    },
    matchId() {
      return this.$route.params.id
    },
    isGenerating() {
      // Yield to reportFailed/isStuck so their v-else-if branches are reachable:
      // - If reportState=FAILED but state=ACCEPTED (pipeline failed before SCORED),
      //   this would otherwise stay true and mask the error banner.
      // - If polling exhausted, defer to isStuck so the retry banner can show.
      if (this.reportFailed || this.pollExhausted) return false
      return (this.match && ['ACCEPTED', 'SCORED'].includes(this.match.state)) ||
        ['PENDING', 'IN_PROGRESS'].includes(this.match?.reportState)
    },
    reportFailed() {
      return this.match?.reportState === 'FAILED'
    },
    // True when polling exhausted but match is still not DONE and not explicitly FAILED
    // (pipeline may be stuck in ACCEPTED or SCORED state without a reportState update)
    isStuck() {
      return this.pollExhausted &&
        !this.reportFailed &&
        this.match?.state !== 'DONE'
    },
    isInitiator() {
      return this.match?.initiatorId === this.store.user?.user_id
    },

    myRate() {
      if (!this.match) return null
      return this.isInitiator ? this.match.initiatorRate : this.match.recipientRate
    },

    myRateComment() {
      if (!this.match) return null
      return this.isInitiator ? this.match.initiatorRateComment : this.match.recipientRateComment
    },

    otherPersonName() {
      if (!this.match) return ''
      const userId = this.store.user?.user_id
      if (this.match.initiatorId === userId) {
        const r = this.match.recipient
        return r ? `${r.firstName} ${r.lastName || ''}`.trim() : this.match.recipientFirstName
      }
      const i = this.match.initiator
      return i ? `${i.firstName} ${i.lastName || ''}`.trim() : ''
    },
    compatColor() {
      const level = this.match?.scores?.compatLevel
      if (level === 'COMPAT_OPTIM')  return 'success'
      if (level === 'COMPAT_MINIM')  return 'warning'
      return 'default'
    },
    compatLabel() {
      const level = this.match?.scores?.compatLevel
      if (level === 'COMPAT_OPTIM')  return 'Compatibilitate optimă'
      if (level === 'COMPAT_MINIM')  return 'Compatibilitate minimă'
      return 'Compatibilitate scăzută'
    },
    /**
     * Returns one entry per scored dimension, omitting any that are absent from
     * the match scores (e.g. a dimension not yet computed).
     *
     * @returns {{ key: string, label: string, pct: number, alert: boolean, alertColor: string }[]}
     *   key        – dimension identifier ('val' | 'b5' | 'att' | 'com' | 'age')
     *   label      – human-readable name (Romanian)
     *   pct        – score rounded to whole percent (0–100)
     *   alert      – true when score falls below the dimension's alert threshold
     *   alertColor – Vuetify colour token used for the alert state ('warning')
     */
    scoreBreakdown() {
      const c = this.match?.scores?.components
      if (!c) return []
      const pct = (v) => v != null ? Math.round(v * 100) : null
      const dims = [
        { key: 'val', label: 'Valori comune',      value: c.val, alertThreshold: 0.35 },
        { key: 'b5',  label: 'Personalitate',       value: c.b5,  alertThreshold: null },
        { key: 'att', label: 'Atașament',            value: c.att, alertThreshold: 0.40 },
        { key: 'com', label: 'Comunicare',           value: c.com, alertThreshold: null },
        { key: 'age', label: 'Etapă de viață',      value: c.age, alertThreshold: null },
      ]
      return dims
        .filter(d => d.value != null)
        .map(d => ({
          key:        d.key,
          label:      d.label,
          pct:        pct(d.value),
          alert:      d.alertThreshold != null && d.value < d.alertThreshold,
          alertColor: 'warning',
        }))
    },
    
    scoreMap() {
      return Object.fromEntries(this.scoreBreakdown.map(d => [d.key, d]))
    },

    /**
     * Returns perspective-correct vector angles for the ValuesVectorChart.
     * The backend always stores vecAngleA = initiator, vecAngleB = recipient,
     * so when the logged-in user is the recipient we must swap.
     * Returns null for both angles when either is missing.
     *
     * @returns {{ me: number|null, other: number|null }}
     */
    valVectors() {
      const val = this.match?.scores?.details?.val
      if (val?.vecAngleA == null || val?.vecAngleB == null) return { me: null, other: null }
      const isInitiator = this.match.initiatorId === this.store.user?.user_id
      return {
        me:    isInitiator ? val.vecAngleA : val.vecAngleB,
        other: isInitiator ? val.vecAngleB : val.vecAngleA,
      }
    },


    /**
     * Returns shared and opposing top-3 values between the reader and the other person,
     * swapping A/B based on who the logged-in user is (initiator vs recipient).
     *
     * @returns {{ shared: string[], opposing: string[] } | null}
     */
    valueInsights() {
      const val = this.match?.scores?.details?.val
      if (!val?.dimensions || !val.topValuesA || !val.topValuesB) return null

      //find who is
      const isInitiator  = this.match.initiatorId === this.store.user?.user_id

      //find values
      const readerTop    = isInitiator ? val.topValuesA : val.topValuesB
      const otherTop     = isInitiator ? val.topValuesB : val.topValuesA

      // Shared = values both persons have in their top 3 (intersection)
      const shared = readerTop.filter(v => otherTop.includes(v))
      const opposing = otherTop.filter(vB => !shared.includes(vB) && readerTop.some(vA => areOpposed(vA, vB)))

      return { shared, opposing }
    },
  },

  async created() {
    await this.loadMatch()
  },

  beforeUnmount() {
    this.stopPolling()
  },

  methods: {
    async loadMatch() {
      this.loadingMatch = true
      try {
        const data = await apiService.getMatch(this.matchId)
        this.match = data
        // report is perspective-filtered by the backend (initiator vs recipient view)
        this.report = data.report ?? null

        if (this.match.state === 'DONE' && this.report) {
          return  // Already complete — nothing to poll
        }

        // Start polling if the pipeline is in progress or the match was accepted
        // but the report hasn't been generated yet
        if (['ACCEPTED', 'SCORED'].includes(this.match.state) ||
            ['PENDING', 'IN_PROGRESS'].includes(this.match.reportState)) {
          this.startPolling()
        }
      } catch (err) {
        if (err.message?.includes('404') || err.message?.includes('not found')) {
          this.notFound = true
        }
        console.error('MatchReportView > loadMatch error:', err)
      } finally {
        this.loadingMatch = false
      }
    },

    startPolling() {
      this.pollCount = 0
      this.pollTimer = setInterval(async () => {
        this.pollCount++
        try {
          // Use the lightweight status endpoint — avoids fetching the full report JSON on every tick
          const status = await apiService.getMatchStatus(this.matchId)
          // Merge status fields into match so computed props (isGenerating, etc.) react
          this.match = { ...this.match, ...status }

          if (status.reportState === 'DONE') {
            this.stopPolling()
            // Fetch the full match to get the perspective-filtered report payload
            const full = await apiService.getMatch(this.matchId)
            this.match = full
            this.report = full.report ?? null
            return
          }

          if (status.reportState === 'FAILED') {
            this.stopPolling()
            return  // reportFailed computed will show the error banner
          }
        } catch (err) {
          console.error('MatchReportView > poll error:', err)
        }

        // Safety limit: if the pipeline hasn't settled after POLL_MAX ticks, stop
        // polling and surface the isStuck state so the user can manually retry
        if (this.pollCount >= POLL_MAX) {
          this.stopPolling()
          if (this.match?.state !== 'DONE') {
            this.pollExhausted = true
          }
        }
      }, POLL_INTERVAL)
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    md(text) {
      const shifted = text.replace(/^(#{1,4})/gm, '##$1')
      return marked(shifted)
    },

    async unlockPremium() {
      try {
        const { premiumUnlockedAt } = await apiService.unlockPremium()
        this.store.setUser({ ...this.store.user, premiumUnlockedAt })
        this.premiumUnlocked = true
      } catch (err) {
        console.error('MatchReportView > unlockPremium error:', err)
      }
    },

    async retryReport() {
      this.retrying = true
      this.pollExhausted = false  // Clear stuck state so isGenerating shows while retrying
      try {
        await apiService.retryMatchReport(this.matchId)
        // Optimistically mark as PENDING so the generating spinner appears immediately
        this.match = { ...this.match, reportState: 'PENDING' }
        this.startPolling()
      } catch (err) {
        console.error('MatchReportView > retryReport error:', err)
      } finally {
        this.retrying = false
      }
    },
  },
}
</script>

<style scoped>
.md-content :deep(*) { all: revert; }

.report-gated {
  position: relative;
  max-height: 3.5rem;
  overflow: hidden;
}

.report-gated-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2.5rem;
  background: linear-gradient(to bottom, transparent, rgb(var(--v-theme-surface)));
}
</style>
